import {
  OrderSearchContextProps,
  OrderSearchFormInfo,
} from '@euler/app/flows/search/types';
import { DropdownProvider } from '@euler/components/dropdown/context';
import { FormGroupContext } from '@euler/components/form';
import { InputContext, useBaseInputStyle } from '@euler/components/form/Input';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useFormController } from '@euler/lib/form/controller';
import { ReceptionOrderStatus } from '@euler/model/enum';
import { SearchOrderResultItem } from '@euler/model/order/SearchOrderResultItem';
import { ReactNode, createContext, memo, useContext } from 'react';
import { useTheme } from 'styled-components';

export const OrderSearchContext = createContext<OrderSearchContextProps>(
  null as any,
);

export const useOrderSearchContext = () => useContext(OrderSearchContext);

export const useOrderSearchForm = () => useOrderSearchContext().form;

export const OrderSearchProvider = memo(
  ({
    defaultStatus,
    finish,
    openOrderSearchResultItem,
    children,
  }: {
    defaultStatus?: ReceptionOrderStatus;
    finish: () => void;
    openOrderSearchResultItem: (resultItem: SearchOrderResultItem) => void;
    children?: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      order: { labelColor, placeholderColor },
      form: {
        input: { backgroundColor: inputBgColor },
      },
    } = useTheme();

    const form = useFormController<OrderSearchFormInfo>({
      orderNo: {
        defaultValue: '',
      },
      licensePlateNo: {
        defaultValue: '',
      },
      vin: {
        defaultValue: '',
      },
      serviceAgent: {
        defaultValue: undefined,
      },
      mileage: {
        defaultValue: undefined,
      },
      vehicleBrandName: {
        defaultValue: '',
      },
      vehicleModel: {
        defaultValue: '',
      },
      vehicleFuelType: {
        defaultValue: undefined,
      },
      status: {
        defaultValue: defaultStatus,
      },
      createdAt: {
        defaultValue: undefined,
      },
      finishedAt: {
        defaultValue: undefined,
      },
      keyword: {
        defaultValue: '',
      },
      tags: {
        defaultValue: undefined,
      },
      customerName: {
        defaultValue: undefined,
      },
      customerPhone: {
        defaultValue: undefined,
      },
    });

    const baseInputStyle = useBaseInputStyle();

    return (
      <OrderSearchContext.Provider
        value={{
          form,
          finish,
          openOrderSearchResultItem,
        }}
      >
        <InputContext.Provider
          value={{
            borderColor: '#eee',
            borderWidth: rpx(1.5),
            fontSize: rpx(15),
            withNativeClearButton: false,
            containerStyle: {
              backgroundColor: inputBgColor,
              minHeight: rpx(52),
              justifyContent: 'center',
            },
          }}
        >
          <DropdownProvider
            placeholderTextColor={placeholderColor}
            backdropMode="modal"
            backdropBlurTint="dark"
            backdropBlurIntensity={5}
            componentStyles={{
              container: {
                minHeight: rpx(52),
              },
              inputContainer: {
                justifyContent: 'center',
              },
              focusedInputContainer: {
                borderWidth: rpx(1.5),
                borderColor: baseInputStyle.borderColorFocused,
              },
              dropdownContainer: {
                borderRadius: baseInputStyle.borderRadius,
              },
            }}
          >
            <FormGroupContext.Provider
              value={{
                labelStyle: { color: labelColor },
                style: {
                  paddingTop: 0,
                  paddingBottom: 0,
                  marginBottom: rpx(15),
                },
              }}
            >
              {children}
            </FormGroupContext.Provider>
          </DropdownProvider>
        </InputContext.Provider>
      </OrderSearchContext.Provider>
    );
  },
);
